import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { NotebookPen, Plus, Pencil, Trash2, EyeOff } from "lucide-react";

const EMPTY = { current_lesson: "", homework: "", notes: "", visible: true };

function NoteForm({ classId, note, onClose, onSaved }) {
    const [form, setForm] = React.useState(note ? { ...EMPTY, ...note } : EMPTY);
    const [busy, setBusy] = React.useState(false);
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    async function save() {
        setBusy(true);
        const payload = { current_lesson: form.current_lesson, homework: form.homework || null, notes: form.notes || null, visible: form.visible };
        try {
            if (note) await api.put(`/class-notes/${note.id}`, payload);
            else await api.post(`/classes/${classId}/notes`, payload);
            toast.success("Note saved");
            onSaved();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    }
    return (
        <div className="space-y-3">
            <div>
                <Label>Where the class is right now</Label>
                <Textarea rows={3} value={form.current_lesson} onChange={set("current_lesson")} placeholder="e.g. Surah Al-Mulk, ayah 1–10 · Arabic: lesson 7 (past tense)" data-testid="class-note-lesson" />
            </div>
            <div>
                <Label>Homework they had</Label>
                <Textarea rows={3} value={form.homework} onChange={set("homework")} placeholder="e.g. Memorize ayah 1–5, worksheet page 12" data-testid="class-note-homework" />
            </div>
            <div>
                <Label>Other notes for the substitute (optional)</Label>
                <Textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Seating, students who need extra help, reminders…" data-testid="class-note-notes" />
            </div>
            <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.visible} onCheckedChange={v => setForm(f => ({ ...f, visible: v }))} data-testid="class-note-visible" />
                Visible to all teachers of this class
            </label>
            <Button onClick={save} disabled={busy || !form.current_lesson.trim()} className="w-full" data-testid="class-note-save">{busy ? "Saving…" : "Save note"}</Button>
        </div>
    );
}

function NoteCard({ n, mine, onEdit, onDelete }) {
    return (
        <div className="rounded-xl border border-border p-4 bg-white" data-testid={`class-note-${n.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-xs text-brand-stone">
                    <b className="text-brand-ink">{n.author_name}</b> · updated {new Date(n.updated_at).toLocaleString()}
                    {!n.visible && <Badge className="ml-2 bg-stone-100 text-stone-700 border-0 text-[10px] gap-1"><EyeOff className="w-3 h-3" /> Only you</Badge>}
                </div>
                {mine && (
                    <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`class-note-edit-${n.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive" data-testid={`class-note-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                )}
            </div>
            <div className="space-y-2 text-sm">
                <div><div className="overline text-brand-stone text-[10px]">Currently learning</div><p className="whitespace-pre-wrap">{n.current_lesson}</p></div>
                {n.homework && <div><div className="overline text-brand-stone text-[10px]">Homework</div><p className="whitespace-pre-wrap">{n.homework}</p></div>}
                {n.notes && <div><div className="overline text-brand-stone text-[10px]">Notes</div><p className="whitespace-pre-wrap">{n.notes}</p></div>}
            </div>
        </div>
    );
}

/** Notes a teacher leaves for a substitute; shared with every teacher assigned to the class. */
export function ClassNotes({ classId }) {
    const { user } = useAuth();
    const [items, setItems] = React.useState([]);
    const [editing, setEditing] = React.useState(null); // null | "new" | note

    const load = React.useCallback(() => {
        if (classId) { api.get(`/classes/${classId}/notes`).then(r => setItems(r.data)); }
    }, [classId]);
    React.useEffect(() => { load(); }, [load]);

    async function remove(n) {
        if (!window.confirm("Delete this note?")) return;
        await api.delete(`/class-notes/${n.id}`);
        toast.success("Note deleted");
        load();
    }
    return (
        <Card>
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <NotebookPen className="w-4 h-4 text-primary" />
                        <h2 className="font-dash text-xl font-semibold">Substitute notes</h2>
                    </div>
                    <Button onClick={() => setEditing("new")} className="gap-1.5" data-testid="class-note-new"><Plus className="w-4 h-4" /> New note</Button>
                </div>
                <p className="text-xs text-brand-stone mb-4">Every teacher assigned to this class can read visible notes.</p>
                <div className="space-y-3" data-testid="class-notes-list">
                    {items.map(n => <NoteCard key={n.id} n={n} mine={n.author_id === user?.id} onEdit={() => setEditing(n)} onDelete={() => remove(n)} />)}
                    {items.length === 0 && <div className="text-center py-10 text-brand-stone text-sm">No notes yet. Leave one before you're away so the substitute knows where to pick up.</div>}
                </div>
                <Dialog open={!!editing} onOpenChange={v => { if (!v) setEditing(null); }}>
                    <DialogContent data-testid="class-note-dialog">
                        <DialogHeader><DialogTitle className="font-serif text-2xl">{editing === "new" ? "New substitute note" : "Edit note"}</DialogTitle></DialogHeader>
                        {editing && <NoteForm classId={classId} note={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={load} />}
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
}
